/**
 * `df-42a9cd00b2ab`: mirroring the global recipe library into the user's own database, and
 * making the meal-planning pages wait until that has happened.
 *
 * What this is for. The meal planner refuses to build a plan when any ingredient of a recipe
 * is missing, so a brand-new paying customer used to see empty rows and nonsense match
 * percentages on their first visit (#3244, #3245). This copies every official recipe and
 * every food those recipes use into the user's database once, and holds the meal-planning
 * pages back until it has finished.
 *
 * It is a small state machine and nothing else: a saved flag that survives a restart, a
 * promise the pages wait on, and a guard so two attempts never run at once. It was lifted out
 * of `food-db.service.ts` because that class pulls in the whole Angular dependency graph and
 * so will not load under the node test runner, which meant none of the decisions below could
 * be covered by a test that actually runs them. Everything it borrows — reading and writing
 * the saved flag, looking in the database, fetching a food — arrives as a plain parameter for
 * exactly that reason.
 *
 * Behaviour is unchanged from what lived in the service, with one addition (#6371): a returning
 * user also gets the official recipes added to the shared database after their first copy. See
 * `topUpAsync` below and `design-docs/local-storage-and-auto-plan-cache.md`, "Recipes added
 * after the first copy".
 */

import type { ComplexFood } from '@cf-shared/data-model/food';
import { isIngredientReal } from '@cf-shared/data-model/ingredient';
import { DeferredPromise, timeout } from '@cf-shared/shared-util';
import { isBackgroundReadNotReady } from 'services/background-read-failure';

/**
 * Name of the saved flag that records "the recipe library has already been copied across".
 * It is stored per web address, so it can outlive the database it describes — an account
 * switch on a shared browser, or the iOS storage migration that clears the database but not
 * this flag. That is why the flag is always checked against what the database actually holds
 * before it is believed (#3316).
 */
export const AUTO_PLAN_FOODS_CACHED_KEY = 'AutoPlanFoodsCached';

/** Every official recipe's id starts with this. */
export const AUTO_PLAN_FOODS_RECIPE_PREFIX = 'food/$OFR-';

/**
 * How many foods are fetched at once. Kept small so the copying does not flood the database's
 * write queue and hold up something the user is waiting on, such as saving a plan while they
 * move around the app.
 */
export const AUTO_PLAN_FOODS_BATCH_SIZE = 10;

/**
 * How long the top-up waits for the list of recipes in the shared database. Before a connection
 * exists, a read of the shared database waits for it to be attached and that wait never ends, so
 * the read is given up on instead. Nothing waits for the top-up, so this only stops a pending
 * read from piling up; the next app start asks again. Same deadline as the freshness read
 * (`design-docs/local-storage-and-auto-plan-cache.md`, "Offline, and the trap in it").
 */
export const AUTO_PLAN_FOODS_TOP_UP_READ_LIMIT_MS = 3_000;

/** Everything this state machine borrows from its caller. */
export interface AutoPlanFoodsCacheDeps {
  /** true when the saved "already copied" flag is set */
  readCachedFlag: () => boolean;
  /** records that the copying finished */
  setCachedFlag: () => void;
  /** forgets the saved flag, so the copying runs again */
  clearCachedFlag: () => void;
  /**
   * true when the user's database really does hold official recipes. Throws the usual
   * database-unavailable error when it could not look at all, which is not the same answer as
   * "there are none".
   */
  hasOfficialRecipesCachedAsync: () => Promise<boolean>;
  /** the recipes to copy across, together with the foods they use */
  readCandidatesAsync: () => Promise<ComplexFood[]>;
  /** fetches one food into the user's database; answers null or undefined when it could not */
  fetchAndCacheFoodAsync: (foodId: string) => Promise<unknown>;
  /** the ids of the official recipes already in the user's database (#6371) */
  listCachedOfficialRecipeIdsAsync: () => Promise<string[]>;
  /** the ids of the official recipes in the shared database (#6371) */
  listSharedOfficialRecipeIdsAsync: () => Promise<string[]>;
  /**
   * reads one food from the shared database WITHOUT copying it into the user's database;
   * answers null or undefined when it could not (#6371)
   */
  readSharedFoodAsync: (foodId: string) => Promise<ComplexFood | null | undefined>;
  /** false once the database has been shut down, for instance because the user signed out */
  isDatabaseAvailable: () => boolean;
  /**
   * told when an attempt failed with an error rather than an empty answer. The next gated
   * visit retries either way; this only makes sure the failure reaches somebody. The caller
   * decides which errors are routine (offline, sign-out) and which are worth a report.
   */
  reportCachingFailure: (err: unknown) => void;
}

/**
 * The saved-flag / waiting-promise / one-attempt-at-a-time state machine described at the top
 * of this file. One of these belongs to one signed-in session.
 */
export class AutoPlanFoodsCache {
  /**
   * Resolves true once the recipe library and every food it uses are in the user's database.
   * The meal-planning pages wait on this before they try to build anything.
   *
   * A failed attempt deliberately leaves it unresolved: the helper below starts another
   * attempt on the next visit, so a session can recover without the app being restarted.
   */
  public readonly whenCached = DeferredPromise.defer<boolean>();
  private cachedResolved = false;
  private cachingInFlight = false;
  private topUpInFlight = false;

  /** how long the top-up waits for the shared recipe list; a field so tests can shorten it */
  public topUpReadLimitMs = AUTO_PLAN_FOODS_TOP_UP_READ_LIMIT_MS;

  constructor(private deps: AutoPlanFoodsCacheDeps) { }

  /** true while a top-up of newly added recipes is running */
  public get isTopUpInFlight(): boolean {
    return this.topUpInFlight;
  }

  /** true once the copying has finished in this session, or was found already done */
  public get isResolved(): boolean {
    return this.cachedResolved;
  }

  /** true while an attempt is running */
  public get isInFlight(): boolean {
    return this.cachingInFlight;
  }

  private resolveCached() {
    if (this.cachedResolved) return;
    this.cachedResolved = true;
    this.whenCached.resolve(true);
  }

  /**
   * Waits for the copying to finish, giving up after `timeoutMs`. Answers true when the
   * recipe library is ready and false when the wait ran out — the pages show a friendly
   * message and go back rather than drawing empty or nonsense rows.
   *
   * When the copying is neither finished nor running, the previous attempt must have given
   * up, so another one is started before waiting. Without that, every later visit in the same
   * session would burn the whole wait on a promise nothing was ever going to resolve, and the
   * message would be misleading because nothing would retry until the app was restarted.
   */
  public async awaitCachedWithTimeoutAsync(timeoutMs: number): Promise<boolean> {
    // if cache is neither populated nor in flight, the previous attempt must
    // have bailed out (empty candidates, partial failure, thrown error). kick
    // off another attempt so this gated visit can recover within the session.
    if (!this.cachedResolved && !this.cachingInFlight) {
      void this.cacheAsync();
    }
    // timeout() cancels its timer when the inner promise resolves first, so
    // this doesn't leak a setTimeout per gated visit. narrow the catch to
    // TimeoutError so a future change that rejects whenCached doesn't get
    // silently misclassified as a timeout.
    try {
      await timeout(timeoutMs, this.whenCached);
      return true;
    } catch (e) {
      if (e?.name === 'TimeoutError') return false;
      throw e;
    }
  }

  /**
   * Copies the recipe library across, unless it has already been done. Callers fire this and
   * forget it, so it never rejects: a failure leaves the saved flag unset and the waiting
   * promise unresolved, and the next attempt starts from the beginning.
   */
  public async cacheAsync() {
    // make sure this is started only once per install
    if (this.deps.readCachedFlag()) {
      // the saved flag can outlive the database it describes, so check it against what the
      // database actually holds before trusting it; otherwise the pages would be let through
      // and would draw blank rows. this read throws when the database cannot be read at all,
      // and rejects with CouchDB's own 401 object when a remote read is refused on resume
      // (df-b99ce9dcabce). callers fire this method and forget, so it never rejects: the gate
      // stays unresolved and the next attempt re-runs the whole check. a failure that is not
      // simply "not reachable right now" is reported first.
      let alreadyCached: boolean;
      try {
        alreadyCached = await this.deps.hasOfficialRecipesCachedAsync();
      } catch (err) {
        if (isBackgroundReadNotReady(err)) {
          console.log('Caching foods for auto-planning: data not reachable right now, will retry later.');
          return;
        }
        console.log('Caching foods for auto-planning: checking the saved flag failed, will retry later:', err);
        this.deps.reportCachingFailure(err);
        return;
      }
      if (alreadyCached) {
        console.log('Caching foods for auto-planning already done.');
        // returning users satisfy the meal-suggestions / meal-plan gate
        // immediately — no need to re-fetch anything.
        this.resolveCached();
        // then copy any recipe added to the shared database since their first copy (#6371).
        // the gate is already open, so nothing the user waits on waits for this
        await this.topUpAsync();
        return;
      }
      console.log('Caching foods for auto-planning: stale flag (no official recipes in user-db), re-running.');
      this.deps.clearCachedFlag();
    }
    // if a previous in-session retry is still running, don't fire another one
    // in parallel — the await-with-timeout helper above relies on this guard.
    if (this.cachingInFlight) {
      console.log('Caching foods for auto-planning already in flight.');
      return;
    }
    this.cachingInFlight = true;
    try {
      await this.runCachingAsync();
    } finally {
      this.cachingInFlight = false;
    }
  }

  private async runCachingAsync() {
    console.log('Caching foods for auto-planning started...');

    try {
      const candidates = await this.deps.readCandidatesAsync();

      // if no candidates returned, food-db's remote likely isn't ready yet
      // (race against init) or the request failed silently inside the wrapper.
      // treat as transient: leave the saved flag and the gate promise both
      // pending so the next gated visit / next launch retries (#3244, #3245).
      if (candidates.length === 0) {
        console.log('Caching foods for auto-planning: no candidates returned (likely food-db not yet ready), will retry on next gated visit.');
        return;
      }

      const foodIds = collectFoodIdsToCache(candidates);

      // same defensive guard for the post-aggregation case: a pool of recipes
      // that all turned out to have only macro/non-real ingredients gives us
      // nothing to fetch — degenerate state, retry on next gated visit.
      if (foodIds.length === 0) {
        console.log('Caching foods for auto-planning: candidates contained no fetchable food IDs, will retry on next gated visit.');
        return;
      }

      // process in small batches to avoid flooding PouchDB's write queue,
      // which would block other database writes (e.g. plan saves during navigation)
      let failedCount = 0;
      console.log(`Caching foods for auto-planning: processing ${foodIds.length} items...`);
      for (let i = 0; i < foodIds.length; i += AUTO_PLAN_FOODS_BATCH_SIZE) {
        // if database was shut down (e.g. user logged out), abort
        if (!this.deps.isDatabaseAvailable()) break;
        let batch = foodIds.slice(i, i + AUTO_PLAN_FOODS_BATCH_SIZE);
        let results = await Promise.all(batch.map(id => this.deps.fetchAndCacheFoodAsync(id)));
        // count nulls (failed fetches — already retried once inside the fetch)
        failedCount += results.filter(r => r == null).length;
      }

      if (failedCount > 0) {
        console.log(`Caching foods for auto-planning ended with ${failedCount} failed items, will retry on next gated visit.`);
        // don't set the saved flag — will retry everything on next gated visit / launch
        return;
      }
    } catch (e) {
      // reading the candidates or other setup failed — retry on next gated visit / launch.
      // the caller decides whether this failure is routine or worth a report (#5191)
      console.log('Caching foods for auto-planning failed, will retry on next gated visit:', e);
      this.deps.reportCachingFailure(e);
      return;
    }

    console.log('Caching foods for auto-planning ended.');
    this.deps.setCachedFlag();
    // signal to gated callers (meal-suggestions, meal-plan) that the local
    // recipe + ingredient graph is now safe to feed to MealTuner.
    this.resolveCached();
  }

  /**
   * Copies the official recipes that were added to the shared database after this user's first
   * copy (#6371). The first copy sets the saved flag, and from then on `cacheAsync` only checks
   * that the user has SOME official recipes, so without this a recipe published later never
   * reached a returning user's meal planner, which reads recipes from their own database only.
   *
   * Compares ids, not counts: a count can match while the two sets differ. Each missing recipe's
   * ingredients are copied first and the recipe itself last, because the planner throws on a
   * recipe whose ingredient is missing. A recipe with an ingredient that could not be fetched is
   * left out, so it is still missing at the next app start and is tried again then.
   *
   * Never rejects: it runs after the gate is open and nobody waits on it.
   */
  public async topUpAsync(): Promise<void> {
    // if a top-up is already running, it is copying the same missing recipes
    if (this.topUpInFlight) return;
    this.topUpInFlight = true;
    try {
      const sharedIds = await timeout(this.topUpReadLimitMs, this.deps.listSharedOfficialRecipeIdsAsync());
      const cachedIds = new Set(await this.deps.listCachedOfficialRecipeIdsAsync());
      const missingIds = sharedIds.filter(id => !cachedIds.has(id));
      if (missingIds.length === 0) return;

      console.log(`Topping up auto-planning recipes: ${missingIds.length} added since the first copy...`);
      let skippedCount = 0;
      // one recipe at a time, so together with its ingredient batches this never floods the
      // database's write queue
      for (const recipeId of missingIds) {
        // if database was shut down (e.g. user logged out), abort
        if (!this.deps.isDatabaseAvailable()) break;
        if (!await this.copyOneRecipeAsync(recipeId)) skippedCount++;
      }
      console.log(`Topping up auto-planning recipes ended, ${skippedCount} left for the next app start.`);
    } catch (e) {
      // offline: the shared list did not arrive in time. the next app start asks again
      if (e?.name === 'TimeoutError') {
        console.log('Topping up auto-planning recipes: shared recipe list did not answer in time, will retry next app start.');
        return;
      }
      console.log('Topping up auto-planning recipes failed, will retry next app start:', e);
      this.deps.reportCachingFailure(e);
    } finally {
      this.topUpInFlight = false;
    }
  }

  /** copies one recipe's ingredients, then the recipe; answers whether the recipe was copied */
  private async copyOneRecipeAsync(recipeId: string): Promise<boolean> {
    const recipe = await this.deps.readSharedFoodAsync(recipeId);
    if (!recipe) return false;
    // a damaged document may carry no ingredient list; it then has no ingredient to fetch
    const safeRecipe = Array.isArray(recipe.ingredients) ? recipe : { ...recipe, ingredients: [] } as ComplexFood;
    const ingredientIds = collectFoodIdsToCache([safeRecipe]).filter(id => id !== recipeId);
    for (let i = 0; i < ingredientIds.length; i += AUTO_PLAN_FOODS_BATCH_SIZE) {
      const batch = ingredientIds.slice(i, i + AUTO_PLAN_FOODS_BATCH_SIZE);
      const results = await Promise.all(batch.map(id => this.deps.fetchAndCacheFoodAsync(id)));
      // if any ingredient is missing, the planner would throw on this recipe, so leave it out
      if (results.some(r => r == null)) return false;
    }
    return (await this.deps.fetchAndCacheFoodAsync(recipeId)) != null;
  }
}

/**
 * Every food that has to be in the user's database before the meal planner can use these
 * recipes: each recipe itself, plus each real ingredient it names. Ingredients that are bare
 * macro amounts rather than real foods have nothing to fetch. Ids are returned once each,
 * in the order they were first seen.
 */
export function collectFoodIdsToCache(candidates: ComplexFood[]): string[] {
  let foodIds: string[] = [];
  let foodPending = {};
  for (let sourceRecipe of candidates) {
    for (let ingredient of sourceRecipe.ingredients) {
      if (isIngredientReal(ingredient)) {
        let foodId = ingredient.foodId;
        if (!foodPending[foodId]) {
          foodIds.push(foodId);
          foodPending[foodId] = true;
        }
      }
    }
    if (!foodPending[sourceRecipe._id]) {
      foodIds.push(sourceRecipe._id);
      foodPending[sourceRecipe._id] = true;
    }
  }
  return foodIds;
}
